import { useCallback, useEffect, useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { useDmPatient } from '../../../../hooks/useDmPatient';
import { sendActivationLink } from '../../../../services/dmPatient';
import { DmShellLayout } from './DmShellLayout';
import { SuccessBanner, ErrorBanner, BlockedNotLinkedBanner } from './DmBanners';
import { SkeletonCard } from './DmSkeletons';
import { DmBtnPrimary, DmBtnSecondary } from './DmFormPrimitives';
import type { ActionRunnerProps } from '../types';

const RESEND_COOLDOWN_SECONDS = 60;

function formatActivationCount(count: number): string {
    if (count === 0) return 'Never';
    if (count === 1) return 'Once';
    return `${count} times`;
}

interface SendActivationLinkLandingProps extends ActionRunnerProps {
    inWorkflow?: boolean;
    onSent?: () => void;
}

function SendActivationLinkLanding({
    patientId,
    onBack,
    inWorkflow = false,
    onSent
}: SendActivationLinkLandingProps) {
    const { data: card, isLoading: cardLoading, refetch: refetchCard } = usePatientCard(patientId);
    const { data: dmPatient, isLoading: dmPatientLoading } = useDmPatient(patientId);
    const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

    const isLinked = card?.monitoring?.dmId != null;
    const email = dmPatient?.email ?? null;
    const phone = dmPatient?.phone ?? null;
    const activationCount = card?.monitoring?.dmLinkActivationCount ?? 0;
    const isLoading = cardLoading || (isLinked && dmPatientLoading);

    const mutation = useMutation({
        mutationFn: () => sendActivationLink(patientId),
        onSuccess: () => {
            setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
            void refetchCard();
            onSent?.();
        }
    });

    useEffect(() => {
        if (resendSecondsLeft <= 0) return;
        const timer = setTimeout(() => setResendSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendSecondsLeft]);

    const handleSend = useCallback(() => {
        mutation.reset();
        mutation.mutate();
    }, [mutation]);

    const errorMessage =
        mutation.error instanceof Error
            ? mutation.error.message
            : mutation.isError
              ? 'An unexpected error occurred.'
              : null;

    const buttonLabel = mutation.isPending
        ? 'Sending…'
        : resendSecondsLeft > 0
          ? `Resend in ${resendSecondsLeft}s`
          : mutation.isSuccess
            ? 'Resend activation link'
            : 'Send activation link';

    const canSend = !mutation.isPending && resendSecondsLeft === 0;

    const content = (
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
            {isLoading && <SkeletonCard />}

            {/*  Defensive: not linked (gating should prevent reaching here)  */}
            {!isLoading && !isLinked && <BlockedNotLinkedBanner onBack={onBack} />}

            {!isLoading && isLinked && (
                <>
                    {mutation.isSuccess && (
                        <SuccessBanner
                            message={`Activation link sent to ${email ?? 'the patient'}. They should receive it within a minute.`}
                        />
                    )}

                    {errorMessage && <ErrorBanner message={errorMessage} onRetry={handleSend} />}

                    {/*  Contact preview card  */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                            Link will be sent to
                        </p>
                        <div className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                            <span className="text-gray-500">Email</span>
                            <span className="text-gray-800">{email ?? '—'}</span>
                            {phone && (
                                <>
                                    <span className="text-gray-500">Phone</span>
                                    <span className="text-gray-800">{phone}</span>
                                </>
                            )}
                            <span className="text-gray-500">Previously sent</span>
                            <span className="text-gray-800">
                                {formatActivationCount(activationCount)}
                            </span>
                        </div>
                        {!email && (
                            <p className="text-xs text-gray-400 mt-3">
                                No email on the Dental Monitoring record. Update the DM patient's
                                email before sending.
                            </p>
                        )}
                    </div>

                    <p className="text-xs text-gray-400">
                        The patient receives an email with a link to install and activate the Dental
                        Monitoring app.
                    </p>

                    {/*  Footer  */}
                    <div className="flex justify-end gap-2 pt-2">
                        {!inWorkflow && (
                            <DmBtnSecondary onClick={onBack} disabled={mutation.isPending}>
                                Cancel
                            </DmBtnSecondary>
                        )}
                        <DmBtnPrimary onClick={handleSend} disabled={!canSend}>
                            <span className="inline-flex items-center gap-2">
                                {!mutation.isPending && <PaperAirplaneIcon className="w-4 h-4" />}
                                {buttonLabel}
                            </span>
                        </DmBtnPrimary>
                    </div>
                </>
            )}
        </div>
    );

    if (inWorkflow) return content;

    return (
        <DmShellLayout title="Send activation link" onBack={onBack}>
            {content}
        </DmShellLayout>
    );
}

export { SendActivationLinkLanding };
